import type { CanvasPoint, Idea, Placement } from '../api/types'
import { isQuickToFilm, STAGES, type Stage } from './stages'

/**
 * Where everything sits on the desktop canvas, in canvas units.
 *
 * The four stages are frames in a row, in the order the work runs. Ideas and
 * Scripted are free: a card stays where Martin drops it, which is how he
 * clusters what belongs together. Until he moves one, a frame lays itself out
 * as a tidy grid, grouped the way the phone list is, and a card that arrives
 * without a place (a new idea, one that changed stage) joins the end. Filming
 * is a list in reading order and To post a list the pipeline fills, so neither
 * stores a place.
 *
 * Everything here is arithmetic on ideas and card sizes, so it is tested
 * without a browser; `Canvas.tsx` measures the titles and draws the result.
 */
export const CARD_WIDTH = 232
export const GAP = 12
export const PAD = 20
const LABEL_HEIGHT = 30
/** Under the Ideas grid, for the line that says a double-click adds one. */
const HINT_HEIGHT = 34
/** Frame labels sit above their frame, and a drop there still counts as the frame. */
export const TAB_HEIGHT = 44

interface Frame {
  x: number
  width: number
  minHeight: number
  /** Free frames: how many columns the tidy grid has. */
  columns?: number
}

const IDEAS_WIDTH = 3 * CARD_WIDTH + 2 * GAP + 2 * PAD
const SCRIPTED_WIDTH = 2 * CARD_WIDTH + GAP + 2 * PAD
const BETWEEN = 110

export const FRAMES: Record<Stage, Frame> = {
  ideas: { x: 0, width: IDEAS_WIDTH, minHeight: 420, columns: 3 },
  scripted: { x: IDEAS_WIDTH + BETWEEN, width: SCRIPTED_WIDTH, minHeight: 360, columns: 2 },
  filming: { x: IDEAS_WIDTH + SCRIPTED_WIDTH + 2 * BETWEEN, width: 300, minHeight: 240 },
  post: { x: IDEAS_WIDTH + SCRIPTED_WIDTH + 300 + 3 * BETWEEN, width: 330, minHeight: 240 },
}

export const isFree = (stage: Stage): stage is 'ideas' | 'scripted' => stage === 'ideas' || stage === 'scripted'

export const cardWidth = (stage: Stage) => (isFree(stage) ? CARD_WIDTH : FRAMES[stage].width - 2 * PAD)

export interface Card {
  id: number
  stage: Stage
  /** Inside the frame. */
  x: number
  y: number
  width: number
  height: number
}

export interface Label {
  stage: Stage
  text: string
  x: number
  y: number
}

export interface Layout {
  cards: Map<number, Card>
  heights: Record<Stage, number>
  labels: Label[]
}

export type HeightOf = (idea: Idea, stage: Stage) => number

export function layOut(stages: Record<Stage, Idea[]>, heightOf: HeightOf): Layout {
  const cards = new Map<number, Card>()
  const heights = {} as Record<Stage, number>
  const labels: Label[] = []

  for (const stage of STAGES) {
    const ideas = stages[stage]
    const frame = FRAMES[stage]
    let bottom = 0
    const place = (idea: Idea, x: number, y: number) => {
      const card = { id: idea.id, stage, x, y, width: cardWidth(stage), height: heightOf(idea, stage) }
      cards.set(idea.id, card)
      bottom = Math.max(bottom, y + card.height)
    }

    if (isFree(stage)) {
      const pinned = ideas.filter((idea) => idea.canvas)
      const loose = ideas.filter((idea) => !idea.canvas)
      for (const idea of pinned) {
        const point = clampToFrame(stage, idea.canvas as CanvasPoint)
        place(idea, point.x, point.y)
      }
      // Grouped, with labels, only while nothing has been placed by hand: once
      // Martin arranges a frame, his arrangement is the grouping.
      const groups: [string | null, Idea[]][] =
        pinned.length === 0 && stage === 'ideas'
          ? [
              ['Quick to film', loose.filter(isQuickToFilm)],
              ['Needs a script', loose.filter((idea) => !isQuickToFilm(idea))],
            ]
          : [[null, loose]]
      let y = pinned.length ? bottom + GAP * 2 : PAD
      for (const [text, group] of groups) {
        if (!group.length) continue
        if (text) {
          labels.push({ stage, text, x: PAD + 2, y })
          y += LABEL_HEIGHT
        }
        for (let row = 0; row < group.length; row += frame.columns ?? 1) {
          const inRow = group.slice(row, row + (frame.columns ?? 1))
          inRow.forEach((idea, column) => {
            place(idea, PAD + column * (CARD_WIDTH + GAP), y)
          })
          y += Math.max(...inRow.map((idea) => heightOf(idea, stage))) + GAP
        }
        y += 16
      }
      const hint = stage === 'ideas' ? HINT_HEIGHT : 0
      heights[stage] = Math.max(frame.minHeight, bottom + PAD + hint)
    } else {
      let y = PAD
      for (const idea of ideas) {
        place(idea, PAD, y)
        y += heightOf(idea, stage) + GAP
      }
      heights[stage] = Math.max(frame.minHeight, bottom + PAD)
    }
  }
  return { cards, heights, labels }
}

/** A card's top left on the canvas as a whole. */
export function absolute(card: Pick<Card, 'stage' | 'x' | 'y'>): CanvasPoint {
  return { x: FRAMES[card.stage].x + card.x, y: card.y }
}

/** A point inside a free frame, pulled back so the card stays inside it. */
export function clampToFrame(stage: Stage, point: CanvasPoint): CanvasPoint {
  const frame = FRAMES[stage]
  return {
    x: Math.round(Math.min(Math.max(point.x, PAD / 2), frame.width - CARD_WIDTH - PAD / 2)),
    y: Math.round(Math.max(point.y, PAD / 2)),
  }
}

/** The frame under a point on the canvas, its label included. */
export function stageAt(point: CanvasPoint, heights: Record<Stage, number>): Stage | null {
  for (const stage of STAGES) {
    const frame = FRAMES[stage]
    const inside =
      point.x >= frame.x &&
      point.x <= frame.x + frame.width &&
      point.y >= -TAB_HEIGHT &&
      point.y <= heights[stage]
    if (inside) return stage
  }
  return null
}

/**
 * Where a dropped card lands in a free frame: where it was let go, nudged
 * down until it covers no other card.
 */
export function freeSpot(layout: Layout, stage: Stage, id: number, point: CanvasPoint, height: number): CanvasPoint {
  const { x, y: start } = clampToFrame(stage, point)
  const others = [...layout.cards.values()].filter((card) => card.stage === stage && card.id !== id)
  const overlaps = (y: number) =>
    others.some(
      (card) =>
        x < card.x + card.width + GAP / 2 &&
        x + CARD_WIDTH + GAP / 2 > card.x &&
        y < card.y + card.height + GAP / 2 &&
        y + height + GAP / 2 > card.y,
    )
  let y = start
  while (overlaps(y)) y += 6
  return { x, y }
}

/** Where in the batch a card dropped at `y` goes: before the first card whose middle is below it. */
export function insertionIndex(layout: Layout, queue: readonly number[], y: number): number {
  const index = queue.findIndex((id) => {
    const card = layout.cards.get(id)
    return card !== undefined && y < card.y + card.height / 2
  })
  return index === -1 ? queue.length : index
}

/**
 * Every card of a frame where it is drawn now. The first card Martin moves by
 * hand pins the whole frame, so the rest stay put instead of closing up the
 * gap it left in the grid.
 */
export function pinFrame(layout: Layout, stage: Stage): Placement[] {
  return [...layout.cards.values()]
    .filter((card) => card.stage === stage)
    .map((card) => ({ id: card.id, canvas: { x: card.x, y: card.y } }))
}

/** The right and bottom edges of everything drawn, for fitting the view. */
export function extent(heights: Record<Stage, number>): { width: number; height: number } {
  const last = FRAMES.post
  return { width: last.x + last.width, height: Math.max(...STAGES.map((stage) => heights[stage])) + TAB_HEIGHT }
}
